import { fill, t, type StringKey } from '../strings';
import { PHOTO_KIND_MAX_LENGTH, cleanPhotoKind } from './equipmentPhotos';
import type { AskClaudeQuestionView, DiscoveryReportView } from './askClaude';

/**
 * Discovery (piece 19, *The Discovery card*; decision 5450, row T2750 batch 1).
 * 🔒 Discovery is the stage between Intent and Evaluation, and a record's
 * Discovery is guided by templates from a library: each template asks its
 * questions and names its upload points. An answer is a `Note` on the record
 * whose section key is `template/question`, and an upload is a `File` carrying
 * `discoverySpot` as `template/spot`.
 *
 * 🔒 The card is one universal piece bound to a record and a stage by its props
 * (the owner's answer M1b), so the web and the phone read this one shape and
 * the card moves to another record kind without a rewrite.
 */

export const DISCOVERY_STAGE = 'Discovery' as const;

export const DISCOVERY_QUESTION_KINDS = ['Text', 'Number', 'Choice', 'YesNo'] as const;
export type DiscoveryQuestionKind = (typeof DISCOVERY_QUESTION_KINDS)[number];

export const DISCOVERY_UPLOAD_KINDS = ['Photo', 'File'] as const;
export type DiscoveryUploadKind = (typeof DISCOVERY_UPLOAD_KINDS)[number];

/**
 * 🔒 Decision 5550 (T2770): a template's items beside its own questions and upload points. A Group is one framed
 * block under its heading; a List is a numbered run of items made from its name, *Power source 1*, *Power source 2*.
 * 🔒 Decision 6110 (T2857): a Group or a List stands in a Group, or in each item of a List, by its `parentKey`.
 */
export const DISCOVERY_GROUP_KINDS = ['Group', 'List'] as const;
export type DiscoveryGroupKind = (typeof DISCOVERY_GROUP_KINDS)[number];

/** 🔒 Decision 5550: the most items a List starts a new Deal with. */
export const DISCOVERY_LIST_START_MAX = 20;

/** 🔒 Decision 5550: the most items one List holds on one record. */
export const DISCOVERY_LIST_MAX = 50;

/** 🔒 Decision 6110: the deepest a block stands, a block in a block in a block. */
export const DISCOVERY_BLOCK_DEPTH_MAX = 3;

/** 🔒 Decision 5550: an option named Other opens a box under it for the person's own words, on One and Many alike. */
export const DISCOVERY_OTHER = 'Other';

/** 🔒 The two answers a Yes or No question takes, stored as the note's text. */
export const DISCOVERY_YES = 'Yes';
export const DISCOVERY_NO = 'No';

/** The record kinds a template may attach to today: Deal alone (decision 5450). */
export const DISCOVERY_RECORD_KINDS = ['Deal'] as const;
export type DiscoveryRecordKind = (typeof DISCOVERY_RECORD_KINDS)[number];

// ---- Routes ----

/** The Settings page, under Settings › CRM. */
export const DISCOVERY_TEMPLATES_ROUTE = '/settings/crm/discovery-templates';
/** The library's list and its add, relative to the API root. */
export const DISCOVERY_TEMPLATES_PATH = '/discovery-templates';

export function discoveryTemplatePath(id: string): string {
  return `${DISCOVERY_TEMPLATES_PATH}/${encodeURIComponent(id)}`;
}

/** A template's deactivation and its reactivation. */
export function discoveryTemplateActivePath(id: string): string {
  return `${discoveryTemplatePath(id)}/active`;
}

/** A Deal's Discovery: the card's own routes, under the Deal. */
export function dealDiscoveryPath(dealId: string): string {
  return `/deals/${encodeURIComponent(dealId)}/discovery`;
}

/** Add Template: one link from the Deal to a template. */
export function dealDiscoveryTemplatesPath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/templates`;
}

/** One answer, written as the question's tagged note. */
export function dealDiscoveryAnswerPath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/answers`;
}

/** The stamp that names the upload point a file was taken for. */
export function dealDiscoveryUploadPath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/uploads`;
}

/** 🔒 Decision 5550: Add and a List's name, or a Group taken off and added back: one item onto a section. */
export function dealDiscoveryEntriesPath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/entries`;
}

/** 🔒 Decision 5550: one Group or List item taken off a section. */
export function dealDiscoveryEntryRemovePath(dealId: string): string {
  return `${dealDiscoveryEntriesPath(dealId)}/remove`;
}

/** 🔒 Decision 5551 (T2773): a section taken off the Deal, its answers and files kept plain or removed. */
export function dealDiscoveryTemplateRemovePath(dealId: string): string {
  return `${dealDiscoveryTemplatesPath(dealId)}/remove`;
}

/**
 * 🔒 Decision 5551: what a section's Remove does with its answers and files, the person's pick in the confirm. Keep
 * leaves them on the Deal as plain notes and files; Remove deletes the notes and sends the files to the Trash.
 */
export const DISCOVERY_REMOVE_CHOICES = ['Keep', 'Remove'] as const;
export type DiscoveryRemoveChoice = (typeof DISCOVERY_REMOVE_CHOICES)[number];

/** Save as Template, from the Deal's own sections. */
export function dealDiscoverySaveAsTemplatePath(dealId: string): string {
  return `${dealDiscoveryPath(dealId)}/save-as-template`;
}

/**
 * 🔒 The multipart fields a spot's upload carries beside the file (decision
 * 5450): the spot's tag as the photo's kind, in the field batch EP1 names, and
 * the spot itself as `template/spot`.
 */
export const DISCOVERY_PHOTO_KIND_FIELD = 'photoKind';
export const DISCOVERY_SPOT_FIELD = 'discoverySpot';

// ---- Keys ----

/** The separator between a template's key and its question's or spot's key. */
const KEY_SEPARATOR = '/';

/** The separator between a question's or a spot's key and a List item's number (decision 5550). */
const NUMBER_SEPARATOR = '.';

/**
 * The List numbers a key carries: none, one, or one per List on the way in (decision 6110), outermost first.
 * A Group adds no number, so a Group's question keeps the key of the item it stands in.
 */
export type DiscoveryNumbers = number | readonly number[] | null;

function numbersOf(numbers: DiscoveryNumbers): readonly number[] {
  if (numbers === null) return [];
  return typeof numbers === 'number' ? [numbers] : numbers;
}

/**
 * 🔒 A question's, a spot's or a block's key on the Deal: its own key, and a List item's number after a stop,
 * `voltage.2` (decision 5550). Under a List item inside another List item it carries both numbers, outermost first,
 * `rating.1.2` (decision 6110).
 */
export function discoveryItemKey(key: string, numbers: DiscoveryNumbers = null): string {
  return [key, ...numbersOf(numbers)].join(NUMBER_SEPARATOR);
}

/** 🔒 A question's tagged-note section key, `template/question`, and a List item's `template/question.N` (decision 5550). */
export function discoveryNoteKey(templateKey: string, questionKey: string, number: DiscoveryNumbers = null): string {
  return `${templateKey}${KEY_SEPARATOR}${discoveryItemKey(questionKey, number)}`;
}

/** 🔒 An upload's `discoverySpot`, `template/spot`, and a List item's `template/spot.N` (decision 5550). */
export function discoverySpotKey(templateKey: string, spotKey: string, number: DiscoveryNumbers = null): string {
  return `${templateKey}${KEY_SEPARATOR}${discoveryItemKey(spotKey, number)}`;
}

/** 🔒 The mark that opens an upload point's own note key, `template/@spot` (T2771): no question's key holds it. */
const SPOT_NOTE_MARK = '@';

/** 🔒 An upload point's own note, tagged `template/@spot` or a List item's `template/@spot.N` (T2771), as an answer is tagged. */
export function discoverySpotNoteKey(templateKey: string, spotKey: string, number: DiscoveryNumbers = null): string {
  return `${templateKey}${KEY_SEPARATOR}${SPOT_NOTE_MARK}${discoveryItemKey(spotKey, number)}`;
}

/** The spot a point's note key names, `template/spot`, or null where the key names a question. */
export function spotOfNoteKey(noteKey: string): string | null {
  const parsed = parseDiscoveryKey(noteKey);
  if (!parsed || !parsed.itemKey.startsWith(SPOT_NOTE_MARK)) return null;
  return `${parsed.templateKey}${KEY_SEPARATOR}${parsed.itemKey.slice(SPOT_NOTE_MARK.length)}`;
}

/**
 * The halves of a Discovery item key: the question's or the spot's own key, the innermost List item's number or null,
 * and every List number it carries, outermost first (decision 6110).
 */
export function parseDiscoveryItem(itemKey: string): { key: string; number: number | null; numbers: number[] } | null {
  const match = /^([^./@]+)((?:\.[1-9][0-9]{0,5}){0,3})$/.exec(itemKey);
  if (!match) return null;
  const numbers = match[2] ? match[2].slice(1).split(NUMBER_SEPARATOR).map(Number) : [];
  return { key: match[1]!, number: numbers.length > 0 ? numbers[numbers.length - 1]! : null, numbers };
}

/** 🔒 An item's heading on the Deal (decision 5550): a Group's heading, and a List item's name and number, *Power source 2*. */
export function discoveryEntryHeading(heading: string, number: number | null): string {
  return number === null ? heading.trim() : `${heading.trim()} ${number}`;
}

/**
 * 🔒 The label an item's answer note opens with (decision 5550): the List item before the question, *Power source 2 ·
 * Supply voltage and phase*. Under a List item in another List item, both items, outermost first (decision 6110).
 */
export function discoveryItemLabel(label: string, entryHeadings: string | readonly string[] | null): string {
  const headings = entryHeadings === null ? [] : typeof entryHeadings === 'string' ? [entryHeadings] : entryHeadings;
  return [...headings, label.trim()].join(' · ');
}

/**
 * 🔒 T2761: the shape of a key a template row carries, the shape `discoveryKeyOf` makes: lower case letters and digits
 * in words joined by a hyphen. It holds no stop, slash or at sign, which the note-key and item-key parse read as separators.
 */
export function isDiscoveryKeyShape(key: unknown): key is string {
  return typeof key === 'string' && key.length <= 64 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key);
}

/** The two halves of a Discovery key, or null for any other section key, a stage row's sub-stage among them. */
export function parseDiscoveryKey(key: string | null | undefined): { templateKey: string; itemKey: string } | null {
  if (!key) return null;
  const parts = key.split(KEY_SEPARATOR);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { templateKey: parts[0], itemKey: parts[1] };
}

/**
 * A stable key made from a name: lower case, words joined by a hyphen, and a
 * number added where the name's key is already taken. 🔨 Written once and never
 * renamed, so an answer keyed to it survives a relabel.
 */
export function discoveryKeyOf(name: string, taken: Iterable<string> = []): string {
  const base = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48).replace(/-+$/g, '') || 'item';
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const next = `${base}-${n}`;
    if (!used.has(next)) return next;
  }
}

// ---- The template ----

export interface DiscoveryQuestionDraft {
  /** Kept across an edit, so its answers survive; absent on a new question. */
  key?: string | null;
  label: string;
  kind: DiscoveryQuestionKind;
  /** The choices of a Choice question; ignored on every other kind. */
  options: string[];
  /** 🔒 Decision 5550: a Choice question takes many of its options, drawn as check boxes; ignored on every other kind. */
  allowsMany?: boolean;
  /** 🔒 Decision 5550: a Number question's range, either end optional; ignored on every other kind. */
  rangeFrom?: number | null;
  rangeTo?: number | null;
  /** 🔒 Decision 5550: the `ref` of the Group or List the question stands in, or null for the template's own. */
  group?: string | null;
}

export interface DiscoveryUploadPointDraft {
  key?: string | null;
  tag: string;
  kind: DiscoveryUploadKind;
  /** 🔒 T2771: what the spot is, shown under its name as the tile's hint. */
  description?: string | null;
  /** 🔒 T2771: the kind a photo filed here carries, from decision 5440's kinds or typed; ignored on a File point. */
  defaultPhotoKind?: string | null;
  /** 🔒 Decision 5550: the `ref` of the Group or List the upload point stands in, or null for the template's own. */
  group?: string | null;
}

/** 🔒 Decision 5550: a Group or a List in the form. */
export interface DiscoveryGroupDraft {
  /** Kept across an edit, so the answers keyed to its items survive; absent on a new item. */
  key?: string | null;
  /** The form's own name for the item, which its questions and upload points point at: its key once saved. */
  ref: string;
  kind: DiscoveryGroupKind;
  heading: string;
  /** How many items a List starts a new Deal with; ignored on a Group. */
  startCount?: number;
  /** 🔒 Decision 6110: the `ref` of the Group or List it stands in, or null for one standing in the template itself. */
  parent?: string | null;
}

export interface DiscoveryTemplateDraft {
  name: string;
  description: string;
  questions: DiscoveryQuestionDraft[];
  uploadPoints: DiscoveryUploadPointDraft[];
  /** 🔒 Decision 5550: the template's Groups and Lists, in their order. */
  groups?: DiscoveryGroupDraft[];
  sortOrder?: number;
}

export interface DiscoveryQuestionRow {
  key: string;
  label: string;
  kind: DiscoveryQuestionKind;
  options: string[];
  /** 🔒 Decision 5550; the server always sends it, and a row handed none reads One. */
  allowsMany?: boolean;
  rangeFrom?: number | null;
  rangeTo?: number | null;
  /** The key of the Group or List the question stands in, null for the template's own. */
  groupKey?: string | null;
}

export interface DiscoveryUploadPointRow {
  key: string;
  tag: string;
  kind: DiscoveryUploadKind;
  /** 🔒 T2771; the server always sends both, and a row handed neither reads none. */
  description?: string | null;
  defaultPhotoKind?: string | null;
  /** The key of the Group or List the upload point stands in, null for the template's own. */
  groupKey?: string | null;
}

/** 🔒 Decision 5550: a template's Group or List as stored. */
export interface DiscoveryGroupRow {
  key: string;
  kind: DiscoveryGroupKind;
  heading: string;
  /** How many items a List starts a new Deal with, 0 on a Group. */
  startCount: number;
  /** 🔒 Decision 6110: the key of the Group or List it stands in, null for one standing in the template itself. */
  parentKey?: string | null;
}

export interface DiscoveryTemplateRow {
  id: string;
  version: number;
  key: string;
  name: string;
  description: string | null;
  recordKinds: string[];
  isActive: boolean;
  sortOrder: number;
  /** The Deal whose sections Save as Template copied, null on a seeded template or one made in Settings. */
  madeFromDealId: string | null;
  questions: DiscoveryQuestionRow[];
  uploadPoints: DiscoveryUploadPointRow[];
  /** 🔒 Decision 5550: its Groups and Lists in their order; the server always sends them. */
  groups?: DiscoveryGroupRow[];
}

export interface DiscoveryTemplatesAnswer {
  rows: DiscoveryTemplateRow[];
}

/** The longest name and tag a template takes. */
export const DISCOVERY_NAME_MAX = 80;

/** 🔒 T2771: the longest description an upload point takes. */
export const DISCOVERY_HINT_MAX = 200;

/** 🔒 A Discovery sentence from the one lookup, its `{name}` placeholders filled: the page's refusals and the server's alike. */
export function discoveryText(key: StringKey, values: Readonly<Record<string, string | number>> = {}): string {
  return fill(t(key), values);
}

/** 🔒 The sentences a draft is refused in, the page's and the server's alike. */
export function discoveryTemplateProblems(draft: DiscoveryTemplateDraft): string[] {
  const problems: string[] = [];
  const name = (draft.name ?? '').trim();
  if (!name) problems.push(t('discovery.problem.nameMissing'));
  else if (name.length > DISCOVERY_NAME_MAX) problems.push(discoveryText('discovery.problem.nameLong', { max: DISCOVERY_NAME_MAX }));
  const questions = Array.isArray(draft.questions) ? draft.questions : [];
  const uploads = Array.isArray(draft.uploadPoints) ? draft.uploadPoints : [];
  if (questions.length === 0 && uploads.length === 0) problems.push(t('discovery.problem.empty'));
  const groups = Array.isArray(draft.groups) ? draft.groups : [];
  const refs = new Set(groups.map((group) => group.ref));
  questions.forEach((question, index) => {
    if (question.key && !isDiscoveryKeyShape(question.key)) problems.push(discoveryText('discovery.problem.questionKey', { n: index + 1 }));
    if (!(question.label ?? '').trim()) problems.push(discoveryText('discovery.problem.questionLabel', { n: index + 1 }));
    if (!(DISCOVERY_QUESTION_KINDS as readonly string[]).includes(question.kind)) problems.push(discoveryText('discovery.problem.questionKind', { n: index + 1 }));
    if (question.kind === 'Choice' && cleanOptions(question.options).length < 2) problems.push(discoveryText('discovery.problem.questionOptions', { n: index + 1 }));
    // 🔒 Decision 5550: a Number's range reads From no higher than To.
    if (question.kind === 'Number' && rangeEnd(question.rangeFrom) !== null && rangeEnd(question.rangeTo) !== null && rangeEnd(question.rangeFrom)! > rangeEnd(question.rangeTo)!) problems.push(discoveryText('discovery.problem.questionRange', { n: index + 1 }));
    if (question.group && !refs.has(question.group)) problems.push(discoveryText('discovery.problem.questionGroup', { n: index + 1 }));
  });
  uploads.forEach((point, index) => {
    if (point.key && !isDiscoveryKeyShape(point.key)) problems.push(discoveryText('discovery.problem.uploadKey', { n: index + 1 }));
    const tag = (point.tag ?? '').trim();
    if (!tag) problems.push(discoveryText('discovery.problem.uploadTag', { n: index + 1 }));
    else if (tag.length > DISCOVERY_NAME_MAX) problems.push(discoveryText('discovery.problem.uploadTagLong', { n: index + 1, max: DISCOVERY_NAME_MAX }));
    if (!(DISCOVERY_UPLOAD_KINDS as readonly string[]).includes(point.kind)) problems.push(discoveryText('discovery.problem.uploadKind', { n: index + 1 }));
    // 🔒 T2771: a description of at most two hundred characters, and a default kind of at most sixty.
    if ((point.description ?? '').trim().length > DISCOVERY_HINT_MAX) problems.push(discoveryText('discovery.problem.uploadHintLong', { n: index + 1, max: DISCOVERY_HINT_MAX }));
    if ((cleanPhotoKind(point.defaultPhotoKind) ?? '').length > PHOTO_KIND_MAX_LENGTH) problems.push(discoveryText('discovery.problem.uploadKindLong', { n: index + 1, max: PHOTO_KIND_MAX_LENGTH }));
    if (point.group && !refs.has(point.group)) problems.push(discoveryText('discovery.problem.uploadGroup', { n: index + 1 }));
  });
  // 🔒 Decision 6110: a block stands in a block the template holds, never in itself or one standing in it, at most three deep.
  const tree = groups.map((group) => ({ key: group.ref, parentKey: group.parent ?? null }));
  // 🔒 Decision 5550: each Group or List has a heading, holds at least one question, upload point or block, and a List starts with at most twenty items.
  groups.forEach((group, index) => {
    if (group.key && !isDiscoveryKeyShape(group.key)) problems.push(discoveryText('discovery.problem.groupKey', { n: index + 1 }));
    if (group.parent && (!refs.has(group.parent) || blockAndBelow(tree, group.ref).includes(group.parent))) problems.push(discoveryText('discovery.problem.groupParent', { n: index + 1 }));
    else if (blockDepth(tree, group.ref) > DISCOVERY_BLOCK_DEPTH_MAX) problems.push(discoveryText('discovery.problem.groupDepth', { n: index + 1, max: DISCOVERY_BLOCK_DEPTH_MAX }));
    const heading = (group.heading ?? '').trim();
    if (!heading) problems.push(discoveryText('discovery.problem.groupHeading', { n: index + 1 }));
    else if (heading.length > DISCOVERY_NAME_MAX) problems.push(discoveryText('discovery.problem.groupHeadingLong', { n: index + 1, max: DISCOVERY_NAME_MAX }));
    if (!(DISCOVERY_GROUP_KINDS as readonly string[]).includes(group.kind)) problems.push(discoveryText('discovery.problem.groupKind', { n: index + 1 }));
    if (!questions.some((question) => question.group === group.ref) && !uploads.some((point) => point.group === group.ref) && !groups.some((inner) => inner.parent === group.ref)) problems.push(discoveryText('discovery.problem.groupEmpty', { n: index + 1 }));
    const start = group.startCount ?? 0;
    if (group.kind === 'List' && (!Number.isInteger(start) || start < 0 || start > DISCOVERY_LIST_START_MAX)) problems.push(discoveryText('discovery.problem.groupStart', { n: index + 1, max: DISCOVERY_LIST_START_MAX }));
  });
  return problems;
}

/** 🔒 Decision 6110: a block as the tree reads it, its key and the key of the block it stands in. */
export interface DiscoveryBlockNode {
  key: string;
  parentKey?: string | null;
}

/** The block a block stands in, null where it stands in the template itself or names a block the template does not hold. */
function parentOf(blocks: readonly DiscoveryBlockNode[], key: string): string | null {
  const parent = blocks.find((block) => block.key === key)?.parentKey ?? null;
  return parent !== null && parent !== key && blocks.some((block) => block.key === parent) ? parent : null;
}

/** 🔒 Decision 6110: how deep a block stands, 1 in the template itself; a loop reads deeper than any block may stand. */
export function blockDepth(blocks: readonly DiscoveryBlockNode[], key: string): number {
  let depth = 1;
  for (let at = parentOf(blocks, key); at !== null; at = parentOf(blocks, at)) {
    depth += 1;
    if (depth > blocks.length) return blocks.length + 1;
  }
  return depth;
}

/** 🔒 Decision 6110: a block and every block standing in it at any depth, the ones its Stands In never offers. */
export function blockAndBelow(blocks: readonly DiscoveryBlockNode[], key: string): string[] {
  const found = [key];
  for (let at = 0; at < found.length; at += 1) {
    for (const block of blocks) if (block.parentKey === found[at] && !found.includes(block.key)) found.push(block.key);
  }
  return found;
}

/** 🔒 Decision 6110: how many blocks deep a block's own tree reaches, 1 for a block holding none. */
export function blockHeight(blocks: readonly DiscoveryBlockNode[], key: string): number {
  return Math.max(...blockAndBelow(blocks, key).map((below) => blockDepth(blocks, below))) - blockDepth(blocks, key) + 1;
}

/**
 * 🔒 Decision 6110: the blocks a block's Stands In offers, the template itself aside: every block but the block and
 * the blocks below it, and none where the block's own tree would stand more than three deep.
 */
export function blockParentsOffered(blocks: readonly DiscoveryBlockNode[], key: string): string[] {
  const below = blockAndBelow(blocks, key);
  const height = blockHeight(blocks, key);
  return blocks.filter((block) => !below.includes(block.key) && blockDepth(blocks, block.key) + height <= DISCOVERY_BLOCK_DEPTH_MAX).map((block) => block.key);
}

/** A range end as a finite number, or null where it is blank. */
function rangeEnd(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** A Choice question's options, trimmed, empty ones dropped and each kept once. */
export function cleanOptions(options: readonly string[] | null | undefined): string[] {
  return [...new Set((options ?? []).map((option) => option.trim()).filter((option) => option !== ''))];
}

/**
 * 🔒 The keys a saved draft carries: a question or a spot keeps the key it had,
 * and a new one takes a key made from its label or tag, unique within the
 * template (decision 5450). 🔒 T2761: a key not of the key shape is not kept; the
 * server refuses it first, and this makes a new one where it is called alone.
 */
export function keyedDraft(draft: DiscoveryTemplateDraft): { questions: DiscoveryQuestionRow[]; uploadPoints: DiscoveryUploadPointRow[]; groups: DiscoveryGroupRow[] } {
  // 🔒 Decision 5550: a Group or List keeps its key and a new one takes a key made from its heading; its questions and upload points name it by its form ref.
  const draftGroups = draft.groups ?? [];
  const groupKeys = new Set(draftGroups.flatMap((group) => (isDiscoveryKeyShape(group.key) ? [group.key] : [])));
  const seenGroups = new Set<string>();
  const keyOfRef = new Map<string, string>();
  const groups = draftGroups.map((group) => {
    let key = isDiscoveryKeyShape(group.key) && !seenGroups.has(group.key) ? group.key : '';
    if (!key) {
      key = discoveryKeyOf(group.heading, [...groupKeys, ...seenGroups]);
      groupKeys.add(key);
    }
    seenGroups.add(key);
    keyOfRef.set(group.ref, key);
    return { key, kind: group.kind, heading: group.heading.trim(), startCount: group.kind === 'List' ? Math.max(0, Math.trunc(group.startCount ?? 0)) : 0, parent: group.parent ?? null };
  });
  const groupOf = (ref: string | null | undefined) => (ref ? keyOfRef.get(ref) ?? null : null);
  // 🔒 Decision 6110: a block names the block it stands in by its key once saved.
  const keyedGroups = groups.map(({ parent, ...group }) => ({ ...group, parentKey: groupOf(parent) }));
  const questionKeys = new Set(draft.questions.flatMap((question) => (isDiscoveryKeyShape(question.key) ? [question.key] : [])));
  const spotKeys = new Set(draft.uploadPoints.flatMap((point) => (isDiscoveryKeyShape(point.key) ? [point.key] : [])));
  const seenQuestions = new Set<string>();
  const seenSpots = new Set<string>();
  const questions = draft.questions.map((question) => {
    let key = isDiscoveryKeyShape(question.key) && !seenQuestions.has(question.key) ? question.key : '';
    if (!key) {
      key = discoveryKeyOf(question.label, [...questionKeys, ...seenQuestions]);
      questionKeys.add(key);
    }
    seenQuestions.add(key);
    return {
      key, label: question.label.trim(), kind: question.kind, options: question.kind === 'Choice' ? cleanOptions(question.options) : [],
      allowsMany: question.kind === 'Choice' && question.allowsMany === true,
      rangeFrom: question.kind === 'Number' ? rangeEnd(question.rangeFrom) : null,
      rangeTo: question.kind === 'Number' ? rangeEnd(question.rangeTo) : null,
      groupKey: groupOf(question.group),
    };
  });
  const uploadPoints = draft.uploadPoints.map((point) => {
    let key = isDiscoveryKeyShape(point.key) && !seenSpots.has(point.key) ? point.key : '';
    if (!key) {
      key = discoveryKeyOf(point.tag, [...spotKeys, ...seenSpots]);
      spotKeys.add(key);
    }
    seenSpots.add(key);
    return {
      key, tag: point.tag.trim(), kind: point.kind, groupKey: groupOf(point.group),
      description: (point.description ?? '').trim() || null, defaultPhotoKind: point.kind === 'Photo' ? cleanPhotoKind(point.defaultPhotoKind) : null,
    };
  });
  return { questions, uploadPoints, groups: keyedGroups };
}

/** Moves the row at `index` one place up or down, the Settings form's Move Up and Move Down. */
export function discoveryMoved<T>(rows: readonly T[], index: number, step: -1 | 1): T[] {
  const to = index + step;
  if (index < 0 || index >= rows.length || to < 0 || to >= rows.length) return [...rows];
  const next = [...rows];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

// ---- An answer ----

/**
 * 🔒 Why an answer is refused, or null where it is taken: a Number reads as a
 * number, a Choice is one of its options, and a Yes or No is one of the two. An
 * empty answer is refused, because a note is history and is never blanked.
 */
export function discoveryAnswerProblem(question: Pick<DiscoveryQuestionRow, 'label' | 'kind' | 'options' | 'allowsMany' | 'rangeFrom' | 'rangeTo'>, value: string): string | null {
  const text = value.trim();
  if (!text) return discoveryText('discovery.answer.missing', { label: question.label });
  if (question.kind === 'Number') {
    const number = Number(text.replace(/,/g, ''));
    if (!Number.isFinite(number)) return discoveryText('discovery.answer.number', { label: question.label });
    // 🔒 Decision 5550: a value outside the question's range is refused in a sentence naming it.
    const from = rangeEnd(question.rangeFrom);
    const to = rangeEnd(question.rangeTo);
    if ((from !== null && number < from) || (to !== null && number > to)) return discoveryText('discovery.answer.range', { label: question.label, range: discoveryRangeText(from, to) });
  }
  if (question.kind === 'Choice') {
    const read = discoveryChoicePicks(question.options, text);
    if (read.rest !== '' || read.picks.length === 0) return discoveryText(question.allowsMany ? 'discovery.answer.choiceMany' : 'discovery.answer.choice', { label: question.label });
    if (!question.allowsMany && read.picks.length > 1) return discoveryText('discovery.answer.choice', { label: question.label });
  }
  if (question.kind === 'YesNo' && text !== DISCOVERY_YES && text !== DISCOVERY_NO) return discoveryText('discovery.answer.yesNo', { label: question.label });
  return null;
}

/** 🔒 A range in words, *from 1 to 5*, *1 or more*, *5 or less* (decision 5550). */
export function discoveryRangeText(from: number | null | undefined, to: number | null | undefined): string {
  const low = rangeEnd(from);
  const high = rangeEnd(to);
  if (low !== null && high !== null) return discoveryText('discovery.range.between', { from: low, to: high });
  if (low !== null) return discoveryText('discovery.range.atLeast', { from: low });
  if (high !== null) return discoveryText('discovery.range.atMost', { to: high });
  return '';
}

/** Whether an option is the Other that opens a box (decision 5550). */
export function isDiscoveryOther(option: string): boolean {
  return option.trim().toLowerCase() === DISCOVERY_OTHER.toLowerCase();
}

/**
 * 🔒 A Choice answer's words (decision 5550): the picks in the options' order joined by commas, and Other last,
 * with the person's words after *Other:* where they typed any.
 */
export function discoveryChoiceText(options: readonly string[], picks: readonly string[], other: string): string {
  const chosen = new Set(picks);
  const parts = options.filter((option) => chosen.has(option) && !isDiscoveryOther(option));
  const otherOption = options.find((option) => chosen.has(option) && isDiscoveryOther(option));
  if (otherOption) parts.push(other.trim() ? `${otherOption}: ${other.trim()}` : otherOption);
  return parts.join(', ');
}

/**
 * 🔒 A Choice answer read back into its picks and Other's words (decision 5550). `rest` is whatever no option
 * names, empty for an answer the question takes.
 */
export function discoveryChoicePicks(options: readonly string[], value: string | null | undefined): { picks: string[]; other: string; rest: string } {
  let head = (value ?? '').trim();
  const picks: string[] = [];
  let other = '';
  const otherOption = options.find(isDiscoveryOther);
  if (otherOption) {
    const said = `${otherOption}: `;
    const at = head.startsWith(said) ? 0 : head.indexOf(`, ${said}`);
    if (at >= 0) {
      other = head.slice(at === 0 ? said.length : at + 2 + said.length).trim();
      head = head.slice(0, at === 0 ? 0 : at);
      picks.push(otherOption);
    }
  }
  const byLength = [...options].sort((a, b) => b.length - a.length);
  while (head !== '') {
    const option = byLength.find((candidate) => head.startsWith(candidate) && (head.length === candidate.length || head.slice(candidate.length).startsWith(', ')));
    if (!option) break;
    if (!picks.includes(option)) picks.unshift(option);
    head = head.slice(option.length).replace(/^, /, '');
  }
  return { picks: options.filter((option) => picks.includes(option)), other, rest: head };
}

/**
 * 🔒 The note written for an answer opens with its question's label and a colon before the value
 * (decision 5461; T2768), *Incoming voltage: 230 V*, so the answer reads the same wherever the note is read.
 */
export function discoveryAnswerText(label: string, value: string): string {
  return `${label.trim()}: ${value.trim()}`;
}

/** The value an answer's note holds, its label and colon taken off where the note opens with them; an older note is its own value. */
export function discoveryAnswerValue(label: string, text: string): string {
  const prefix = `${label.trim()}: `;
  return text.startsWith(prefix) ? text.slice(prefix.length) : text;
}

// ---- The card ----

const join = (...parts: readonly (string | number)[]) => parts.join(' ');

export interface DiscoveryFileView {
  id: string;
  name: string;
  mimeType: string;
  /** 🔒 T2771: the photo's kind, its note and its position, decision 5440's own fields; the server always sends them. */
  photoKind?: string | null;
  note?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface DiscoveryQuestionView extends DiscoveryQuestionRow {
  /** The section key its notes carry, `template/question`, or a List item's `template/question.N` (decision 5550). */
  noteKey: string;
  /** 🔒 Decision 5550: the label its note opens with, the List item before the question; the question's own label elsewhere. */
  noteLabel?: string;
  /** The newest note's text, the answer; null while none is written. */
  answer: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
}

export interface DiscoverySpotView extends DiscoveryUploadPointRow {
  /** The `discoverySpot` its files carry, `template/spot`. */
  spot: string;
  files: DiscoveryFileView[];
  /** 🔒 T2771: the section key the point's own note carries, `template/@spot`, and the newest such note's words. */
  noteKey?: string;
  note?: string | null;
}

/** 🔒 Decision 5550: one Group, or one List item, standing on the record: its heading, its questions and its tiles. */
export interface DiscoveryEntryView {
  groupKey: string;
  /**
   * 🔒 Decision 6110: the key its entry row carries, which an item's Add and Remove send: the block's key, and under a
   * List item the item's numbers after it, `electrical-components.2`. The server always sends it; one handed none is the block's key.
   */
  entryKey?: string;
  kind: DiscoveryGroupKind;
  /** The Group's heading, or the List item's name and number, *Power source 2*. */
  heading: string;
  /** The List item's number, null on a Group. */
  number: number | null;
  questions: DiscoveryQuestionView[];
  spots: DiscoverySpotView[];
  /** 🔒 Decision 6110: the Groups and Lists standing in this item, drawn inside it; the server always sends them. */
  groups?: DiscoveryGroupView[];
}

/** 🔒 Decision 5550: a template's Group or List on the record, with the items standing; a Group stands once or not at all. */
export interface DiscoveryGroupView {
  key: string;
  /** 🔒 Decision 6110: the key its items' entry rows carry, as on `DiscoveryEntryView`; one handed none is `key`. */
  entryKey?: string;
  kind: DiscoveryGroupKind;
  heading: string;
  startCount: number;
  entries: DiscoveryEntryView[];
}

/** 🔒 Decision 6110: the key a block's or an item's Add and Remove send, its entry key and its own key where none is sent. */
export function entryKeyOf(view: Pick<DiscoveryGroupView, 'key' | 'entryKey'> | Pick<DiscoveryEntryView, 'groupKey' | 'entryKey'>): string {
  return view.entryKey ?? ('key' in view ? view.key : view.groupKey);
}

/** 🔒 Decision 6110: an item's own name on the card's hooks, its entry key and its List number, `electrical-components.1.2`. */
export function entryHookOf(entry: Pick<DiscoveryEntryView, 'groupKey' | 'entryKey' | 'number'>): string {
  return entry.number === null ? entryKeyOf(entry) : `${entryKeyOf(entry)}${NUMBER_SEPARATOR}${entry.number}`;
}

export interface DiscoverySectionView {
  templateId: string;
  templateKey: string;
  name: string;
  description: string | null;
  /** The template's own questions and tiles, standing outside any Group or List. */
  questions: DiscoveryQuestionView[];
  spots: DiscoverySpotView[];
  /** 🔒 Decision 5550: its Groups and Lists in their order; the server always sends them, and a section handed none draws none. */
  groups?: DiscoveryGroupView[];
}

export interface DiscoveryLibraryEntry {
  id: string;
  key: string;
  name: string;
  description: string | null;
}

/**
 * 🔒 The card's one shape (decision 5450): the record and the stage it is bound
 * to, its sections in the order they were added, the active templates not yet
 * added, and whether the viewer writes.
 */
export interface DiscoveryCardAnswer {
  recordType: DiscoveryRecordKind;
  recordId: string;
  stage: typeof DISCOVERY_STAGE;
  sections: DiscoverySectionView[];
  library: DiscoveryLibraryEntry[];
  writes: boolean;
  /** 🔒 Whether the viewer may Save as Template: a Deal writer who also writes the library, an administrator. */
  savesTemplate: boolean;
  /**
   * 🔒 The open questions Ask Claude wrote onto the Deal, in the order they were asked, each with its newest answer
   * (decision 5530). The server always sends the list; a card handed none draws it empty.
   */
  askClaude?: AskClaudeQuestionView[];
  /**
   * 🔒 Whether the viewer's role carries the Ask Claude permission, `deals.askClaude` (decision 6004): Ask Claude,
   * Resubmit and Make Report are drawn only where it is true, and a card handed none draws none of the three.
   */
  asksClaude?: boolean;
  /** 🔒 The Deal's proposal report Make Report wrote, its newest version, or null before the first (decision 6003). */
  report?: DiscoveryReportView | null;
}

export interface DiscoveryCounts {
  templates: number;
  answered: number;
  questions: number;
  filled: number;
  spots: number;
}

/** The answered questions and filled tiles of one set of questions and tiles. */
function countsOf(questions: readonly DiscoveryQuestionView[], spots: readonly DiscoverySpotView[]): Omit<DiscoveryCounts, 'templates'> {
  return {
    answered: questions.filter((question) => question.answer !== null && question.answer.trim() !== '').length,
    questions: questions.length,
    filled: spots.filter((spot) => spot.files.length > 0).length,
    spots: spots.length,
  };
}

/** Every Group and List item standing on a section, in order, each followed by the items standing in it (decision 6110). */
export function sectionEntries(section: Pick<DiscoverySectionView, 'groups'>): DiscoveryEntryView[] {
  return (section.groups ?? []).flatMap((group) => group.entries.flatMap((entry) => [entry, ...sectionEntries(entry)]));
}

/** One section's counts: its questions answered and its upload points holding a file, every standing Group and List item's included (decision 5550). */
export function sectionCounts(section: DiscoverySectionView): DiscoveryCounts {
  const entries = sectionEntries(section);
  return { templates: 1, ...countsOf([...section.questions, ...entries.flatMap((entry) => entry.questions)], [...section.spots, ...entries.flatMap((entry) => entry.spots)]) };
}

/** 🔒 A Group's or a List item's own count line, *1 of 2 questions · 0 of 1 uploads* (decision 5550); a block standing in it counts on its own line (decision 6110). */
export function entryLine(entry: DiscoveryEntryView): string {
  const counts = countsOf(entry.questions, entry.spots);
  return [join(counts.answered, t('discovery.of'), counts.questions, t('discovery.questions')), join(counts.filled, t('discovery.of'), counts.spots, t('discovery.uploads'))].join(' · ');
}

/** 🔒 The Add a List or a Group taken off offers (decision 5550): *Add Power Source*, *Add Panel Environment*; null for a Group standing. */
export function groupAddLabel(group: Pick<DiscoveryGroupView, 'kind' | 'heading' | 'entries'>): string | null {
  if (group.kind === 'Group' && group.entries.length > 0) return null;
  return discoveryText('discovery.addEntry', { name: titleWords(group.heading) });
}

/** A heading in Title Case for a button's words: each word's first letter raised. */
function titleWords(text: string): string {
  return text.trim().split(/\s+/).map((word) => (word ? word[0]!.toUpperCase() + word.slice(1) : word)).join(' ');
}

/** The card's totals over every section. */
export function discoveryCounts(sections: readonly DiscoverySectionView[]): DiscoveryCounts {
  return sections.map(sectionCounts).reduce((sum, one) => ({
    templates: sum.templates + one.templates,
    answered: sum.answered + one.answered,
    questions: sum.questions + one.questions,
    filled: sum.filled + one.filled,
    spots: sum.spots + one.spots,
  }), { templates: 0, answered: 0, questions: 0, filled: 0, spots: 0 });
}


/** 🔒 The card's head line, *Templates: 2 added · 7 of 12 questions answered · 3 of 6 uploads*, or *Templates: none added*. */
export function discoveryHeadLine(sections: readonly DiscoverySectionView[]): string {
  if (sections.length === 0) return join(t('discovery.templatesLead'), t('discovery.noneAdded'));
  const counts = discoveryCounts(sections);
  return [
    join(t('discovery.templatesLead'), counts.templates, t('discovery.added')),
    join(counts.answered, t('discovery.of'), counts.questions, t('discovery.questionsAnswered')),
    join(counts.filled, t('discovery.of'), counts.spots, t('discovery.uploads')),
  ].join(' · ');
}

/** 🔒 A section's own count line, *2 of 6 questions · 3 of 5 uploads*. */
export function sectionLine(section: DiscoverySectionView): string {
  const counts = sectionCounts(section);
  return [join(counts.answered, t('discovery.of'), counts.questions, t('discovery.questions')), join(counts.filled, t('discovery.of'), counts.spots, t('discovery.uploads'))].join(' · ');
}

/** 🔒 Decision 5551: what a section holds that its Remove asks about, its answers (a question answered or a point's own note) and its files. */
export function sectionHeld(section: DiscoverySectionView): { answers: number; files: number } {
  const entries = sectionEntries(section);
  const questions = [...section.questions, ...entries.flatMap((entry) => entry.questions)];
  const spots = [...section.spots, ...entries.flatMap((entry) => entry.spots)];
  return {
    answers: questions.filter((question) => (question.answer ?? '').trim() !== '').length + spots.filter((spot) => (spot.note ?? '').trim() !== '').length,
    files: spots.reduce((sum, spot) => sum + spot.files.length, 0),
  };
}

/** 🔒 Decision 5551: the confirm's count line, *It holds 3 answers and 2 files.*, or *It holds no answers and no files.* */
export function sectionHeldLine(section: DiscoverySectionView): string {
  const held = sectionHeld(section);
  if (held.answers === 0 && held.files === 0) return t('discovery.removeTemplateEmpty');
  return discoveryText('discovery.removeTemplateHolds', {
    answers: join(held.answers, t(held.answers === 1 ? 'discovery.answerOne' : 'discovery.answerMany')),
    files: join(held.files, t(held.files === 1 ? 'discovery.fileOne' : 'discovery.fileMany')),
  });
}

/** 🔒 A tile's line under its tag: *2 photos*, *1 file*, or *None yet*. */
export function spotLine(spot: DiscoverySpotView): string {
  const count = spot.files.length;
  if (count === 0) return t('discovery.noneYet');
  if (spot.kind === 'Photo') return join(count, t(count === 1 ? 'discovery.photoOne' : 'discovery.photoMany'));
  return join(count, t(count === 1 ? 'discovery.fileOne' : 'discovery.fileMany'));
}

/** A tile's spoken name: its act and its tag, *Add Photo · Nameplate*. */
export function spotTileLabel(spot: Pick<DiscoverySpotView, 'kind' | 'tag'>): string {
  return `${t(spot.kind === 'Photo' ? 'discovery.addPhoto' : 'discovery.addFile')} · ${spot.tag}`;
}

/** 🔒 The active templates the library sheet offers: the ones the record does not yet hold (M2a). */
export function libraryOffered(library: readonly DiscoveryLibraryEntry[], sections: readonly DiscoverySectionView[]): DiscoveryLibraryEntry[] {
  const held = new Set(sections.map((section) => section.templateId));
  return library.filter((entry) => !held.has(entry.id));
}

/**
 * 🔒 The two multipart fields a spot's upload carries beside the file
 * (decision 5450): the spot's tag as the photo's kind, and the spot itself.
 */
export function discoveryUploadFields(spot: Pick<DiscoverySpotView, 'tag' | 'spot' | 'defaultPhotoKind'>): [string, string][] {
  return [[DISCOVERY_PHOTO_KIND_FIELD, spotPhotoKind(spot)], [DISCOVERY_SPOT_FIELD, spot.spot]];
}

/** 🔒 T2771: the kind a shot at a spot carries, the point's default kind and its tag where none is set. */
export function spotPhotoKind(spot: Pick<DiscoverySpotView, 'tag' | 'defaultPhotoKind'>): string {
  return cleanPhotoKind(spot.defaultPhotoKind) ?? spot.tag;
}

/** 🔒 T2771: the thumbnails a filled tile draws before *+n more*, which opens the rest. */
export const DISCOVERY_THUMBS_SHOWN = 6;

/** 🔒 Decision 5550: the template a Save as Template copies, its rows as stored. */
export interface DiscoverySaveSource {
  questions: readonly DiscoveryQuestionRow[];
  uploadPoints: readonly DiscoveryUploadPointRow[];
  groups?: readonly DiscoveryGroupRow[];
}

/**
 * 🔒 Save as Template (M4c): a new template carrying every question and upload
 * point of every template the record holds, each once by its label or its tag
 * and its kind, and a Choice's options, within its Group or List (T2761), with no keys, so the new template makes its own. A
 * Group or List is carried once by its kind and heading within the block it
 * stands in (decision 5550), so the nesting is kept (decision 6110).
 */
export function saveAsTemplateDraft(templates: readonly DiscoverySaveSource[], name: string, description: string): DiscoveryTemplateDraft {
  const questions: DiscoveryQuestionDraft[] = [];
  const uploadPoints: DiscoveryUploadPointDraft[] = [];
  const groups: DiscoveryGroupDraft[] = [];
  const refOf = new Map<string, string>();
  const said = new Set<string>();
  for (const template of templates) {
    const local = new Map<string, string>();
    const rows = template.groups ?? [];
    // 🔒 Decision 6110: a block is carried after the block it stands in, so its parent's ref is already made.
    const carry = (group: DiscoveryGroupRow) => {
      if (local.has(group.key)) return;
      const parentKey = parentOf(rows, group.key);
      if (parentKey !== null && !local.has(parentKey)) carry(rows.find((row) => row.key === parentKey)!);
      const parent = parentKey !== null ? local.get(parentKey) ?? null : null;
      const which = `${group.kind}|${parent ?? ''}|${group.heading.trim().toLowerCase()}`;
      let ref = refOf.get(which);
      if (!ref) {
        ref = `group-${groups.length + 1}`;
        refOf.set(which, ref);
        groups.push({ ref, kind: group.kind, heading: group.heading, startCount: group.startCount, parent });
      }
      local.set(group.key, ref);
    };
    for (const group of rows) if (blockDepth(rows, group.key) <= rows.length) carry(group);
    const groupOf = (key: string | null | undefined) => (key ? local.get(key) ?? null : null);
    for (const question of template.questions) {
      const group = groupOf(question.groupKey);
      const which = `q|${group ?? ''}|${question.label.trim().toLowerCase()}|${question.kind}|${cleanOptions(question.options).join('\u0001').toLowerCase()}`;
      if (said.has(which)) continue;
      said.add(which);
      questions.push({ label: question.label, kind: question.kind, options: [...question.options], allowsMany: question.allowsMany ?? false, rangeFrom: question.rangeFrom ?? null, rangeTo: question.rangeTo ?? null, group });
    }
    for (const point of template.uploadPoints) {
      const group = groupOf(point.groupKey);
      const which = `u|${group ?? ''}|${point.tag.trim().toLowerCase()}|${point.kind}`;
      if (said.has(which)) continue;
      said.add(which);
      uploadPoints.push({ tag: point.tag, kind: point.kind, group, description: point.description ?? null, defaultPhotoKind: point.defaultPhotoKind ?? null });
    }
  }
  return { name, description, questions, uploadPoints, groups };
}

// ---- The layout ----

/**
 * 🔒 Decision 5550: an item standing on a record, as its entry row holds it: a Group's one, numbered 1, or a List's
 * numbered one. `groupKey` is the entry key, the block's own key and, under a List item, the item's numbers after
 * it (decision 6110).
 */
export interface DiscoveryEntryRef {
  groupKey: string;
  number: number;
}

/** A template as the layout reads it. */
export interface DiscoveryLayoutTemplate {
  key: string;
  questions: readonly DiscoveryQuestionRow[];
  uploadPoints: readonly DiscoveryUploadPointRow[];
  groups: readonly DiscoveryGroupRow[];
}

export interface DiscoveryLaidQuestion {
  question: DiscoveryQuestionRow;
  noteKey: string;
  noteLabel: string;
}

export interface DiscoveryLaidSpot {
  point: DiscoveryUploadPointRow;
  spot: string;
  /** 🔒 T2771: the point's own note key, `template/@spot` or a List item's `template/@spot.N`, and the label its note opens with. */
  noteKey: string;
  noteLabel: string;
  /** The Group's heading or the List item's, *Power source 2*, null for the template's own. */
  entryHeading: string | null;
  /** 🔒 Decision 6110: every item's heading on the way in, outermost first, which the photo's filed name carries; empty for the template's own. */
  entryPath: string[];
}

export interface DiscoveryLaidEntry {
  number: number | null;
  heading: string;
  questions: DiscoveryLaidQuestion[];
  spots: DiscoveryLaidSpot[];
  /** 🔒 Decision 6110: the blocks standing in this item, with their items standing. */
  groups: DiscoveryLaidBlock[];
}

/** 🔒 A Group or List laid on a record: the key its entry rows carry, and its items standing. */
export interface DiscoveryLaidBlock {
  group: DiscoveryGroupRow;
  entryKey: string;
  entries: DiscoveryLaidEntry[];
}

export interface DiscoveryLayout {
  questions: DiscoveryLaidQuestion[];
  spots: DiscoveryLaidSpot[];
  groups: DiscoveryLaidBlock[];
}

/** 🔒 Decision 6110: where an item stands, the List numbers on the way in and the List items' headings its notes open with. */
interface DiscoveryPlace {
  numbers: readonly number[];
  headings: readonly string[];
  path: readonly string[];
}

const TOP: DiscoveryPlace = { numbers: [], headings: [], path: [] };

/** 🔒 Decision 6110: the blocks standing directly in a block, or in the template itself for null; a block naming a block the template does not hold stands in the template. */
function blocksIn(groups: readonly DiscoveryGroupRow[], parentKey: string | null): DiscoveryGroupRow[] {
  return groups.filter((group) => parentOf(groups, group.key) === parentKey && blockDepth(groups, group.key) <= DISCOVERY_BLOCK_DEPTH_MAX);
}

/** 🔒 Decision 6110: the place inside one item of a block, a List adding its number and its item's heading. */
function placeIn(place: DiscoveryPlace, group: DiscoveryGroupRow, number: number | null, heading: string): DiscoveryPlace {
  const listed = group.kind === 'List' && number !== null;
  return { numbers: listed ? [...place.numbers, number] : place.numbers, headings: listed ? [...place.headings, heading] : place.headings, path: [...place.path, heading] };
}

/**
 * 🔒 Decision 5550: what a template asks on one record, laid out from its entries. The template's own questions and
 * tiles first, then each Group or List in its order with the items standing: a Group's questions answer under
 * `template/question`, and a List item's under `template/question.N`, its note labelled with the item.
 * 🔒 Decision 6110: each item lays out the blocks standing in it after its own rows. A block under a List item carries
 * the item's number in its entry key and in every key below it, `template/question.1.2` under List items 1 and 2.
 */
export function discoveryLayout(template: DiscoveryLayoutTemplate, entries: readonly DiscoveryEntryRef[]): DiscoveryLayout {
  const own = (key: string | null | undefined) => !key || !template.groups.some((group) => group.key === key);
  const laidQuestion = (question: DiscoveryQuestionRow, place: DiscoveryPlace): DiscoveryLaidQuestion => ({
    question, noteKey: discoveryNoteKey(template.key, question.key, place.numbers), noteLabel: discoveryItemLabel(question.label, place.headings),
  });
  const laidSpot = (point: DiscoveryUploadPointRow, place: DiscoveryPlace): DiscoveryLaidSpot => ({
    point, spot: discoverySpotKey(template.key, point.key, place.numbers), entryHeading: place.path[place.path.length - 1] ?? null, entryPath: [...place.path],
    noteKey: discoverySpotNoteKey(template.key, point.key, place.numbers), noteLabel: discoveryItemLabel(point.tag, place.headings),
  });
  const laidBlock = (group: DiscoveryGroupRow, place: DiscoveryPlace): DiscoveryLaidBlock => {
    const entryKey = discoveryItemKey(group.key, place.numbers);
    const numbers = [...new Set(entries.filter((entry) => entry.groupKey === entryKey).map((entry) => entry.number))].sort((a, b) => a - b);
    const standing = group.kind === 'Group' ? numbers.slice(0, 1).map(() => null) : numbers;
    return {
      group, entryKey,
      entries: standing.map((number) => {
        const heading = discoveryEntryHeading(group.heading, number);
        const inner = placeIn(place, group, number, heading);
        return {
          number, heading,
          questions: template.questions.filter((question) => question.groupKey === group.key).map((question) => laidQuestion(question, inner)),
          spots: template.uploadPoints.filter((point) => point.groupKey === group.key).map((point) => laidSpot(point, inner)),
          groups: blocksIn(template.groups, group.key).map((child) => laidBlock(child, inner)),
        };
      }),
    };
  };
  return {
    // 🔨 A question naming an item the template no longer holds stands with the template's own, so nothing it asks is lost.
    questions: template.questions.filter((question) => own(question.groupKey)).map((question) => laidQuestion(question, TOP)),
    spots: template.uploadPoints.filter((point) => own(point.groupKey)).map((point) => laidSpot(point, TOP)),
    groups: blocksIn(template.groups, null).map((group) => laidBlock(group, TOP)),
  };
}

/** Every item a layout lays, each followed by the items standing in it, in order (decision 6110). */
export function layoutEntries(blocks: readonly DiscoveryLaidBlock[]): DiscoveryLaidEntry[] {
  return blocks.flatMap(({ entries }) => entries.flatMap((entry) => [entry, ...layoutEntries(entry.groups)]));
}

/** Every block a layout lays, at any depth, each before the blocks standing in its items (decision 6110). */
export function layoutBlocks(blocks: readonly DiscoveryLaidBlock[]): DiscoveryLaidBlock[] {
  return blocks.flatMap((block) => [block, ...layoutBlocks(block.entries.flatMap((entry) => entry.groups))]);
}

/** Every question a layout asks, its own and each item's, in order. */
export function layoutQuestions(layout: DiscoveryLayout): DiscoveryLaidQuestion[] {
  return [...layout.questions, ...layoutEntries(layout.groups).flatMap((entry) => entry.questions)];
}

/** Every tile a layout draws, its own and each item's, in order. */
export function layoutSpots(layout: DiscoveryLayout): DiscoveryLaidSpot[] {
  return [...layout.spots, ...layoutEntries(layout.groups).flatMap((entry) => entry.spots)];
}

/**
 * 🔒 Decision 6110: an item and every item standing in it, as the entry rows hold them, the rows an item's Remove
 * deletes with it.
 */
export function entryAndBelow(block: DiscoveryLaidBlock, entry: DiscoveryLaidEntry): DiscoveryEntryRef[] {
  const own = { groupKey: block.entryKey, number: entry.number ?? 1 };
  return [own, ...entry.groups.flatMap((child) => child.entries.flatMap((inner) => entryAndBelow(child, inner)))];
}

/**
 * 🔒 Decision 6110: where a point stands under the numbers its spot key carries, the List items' headings on the way in,
 * outermost first; null where the numbers do not fit the Lists it stands in, one number per List.
 */
export function discoveryPlaceOf(groups: readonly DiscoveryGroupRow[], groupKey: string | null | undefined, numbers: readonly number[]): { headings: string[] } | null {
  const chain: DiscoveryGroupRow[] = [];
  for (let at = groupKey && groups.some((group) => group.key === groupKey) ? groupKey : null; at !== null; at = parentOf(groups, at)) {
    chain.unshift(groups.find((group) => group.key === at)!);
    if (chain.length > DISCOVERY_BLOCK_DEPTH_MAX) return null;
  }
  const lists = chain.filter((group) => group.kind === 'List');
  if (lists.length !== numbers.length) return null;
  return { headings: lists.map((group, at) => discoveryEntryHeading(group.heading, numbers[at]!)) };
}

/** 🔒 Decision 5550: the number the next Add of a List takes, one past the highest standing, and 1 on an empty List. */
export function nextEntryNumber(entries: readonly DiscoveryEntryRef[], groupKey: string): number {
  return entries.filter((entry) => entry.groupKey === groupKey).reduce((high, entry) => Math.max(high, entry.number), 0) + 1;
}

/**
 * 🔒 Decision 5550: the entries Add Template writes: one for each Group and a List's starting items. 🔒 Decision 6110:
 * and under each of them, a nested Group's one and a nested List's starting items, each keyed under its parent item.
 */
export function startingEntries(groups: readonly DiscoveryGroupRow[]): DiscoveryEntryRef[] {
  return blocksIn(groups, null).flatMap((group) => startingOf(groups, group, []));
}

/**
 * 🔒 Decision 6110: the entries one block starts with where it stands, under the List numbers on the way in, each item
 * followed by the starting entries of the blocks standing in it.
 */
function startingOf(groups: readonly DiscoveryGroupRow[], group: DiscoveryGroupRow, numbers: readonly number[]): DiscoveryEntryRef[] {
  const items = group.kind === 'Group' ? [1] : Array.from({ length: group.startCount }, (_, at) => at + 1);
  return items.flatMap((number) => [{ groupKey: discoveryItemKey(group.key, numbers), number }, ...startingUnder(groups, group, numbers, number)]);
}

/** 🔒 Decision 6110: the starting entries of the blocks standing in one item, the entries an Add writes beside the item. */
export function startingUnder(groups: readonly DiscoveryGroupRow[], group: DiscoveryGroupRow, numbers: readonly number[], number: number): DiscoveryEntryRef[] {
  const inner = group.kind === 'List' ? [...numbers, number] : numbers;
  return blocksIn(groups, group.key).flatMap((child) => startingOf(groups, child, inner));
}

// ---- The seed ----

export interface DiscoveryTemplateSeed {
  key: string;
  name: string;
  description: string;
  sortOrder: number;
  questions: readonly DiscoveryQuestionRow[];
  uploadPoints: readonly DiscoveryUploadPointRow[];
  /** 🔒 Decision 5550: its Groups and Lists. */
  groups?: readonly DiscoveryGroupRow[];
}

const q = (key: string, label: string, kind: DiscoveryQuestionKind, options: string[] = [], more: Partial<DiscoveryQuestionRow> = {}): DiscoveryQuestionRow => ({ key, label, kind, options, ...more });
const u = (key: string, tag: string, kind: DiscoveryUploadKind, more: Partial<DiscoveryUploadPointRow> = {}): DiscoveryUploadPointRow => ({ key, tag, kind, ...more });
const g = (key: string, kind: DiscoveryGroupKind, heading: string, startCount = 0): DiscoveryGroupRow => ({ key, kind, heading, startCount });

/**
 * 🔒 The seven templates seeded from the owner's controls-scope-proposal skill
 * (decision 5450): its Inputs, its Step 2 questions and its Do Not Assume list,
 * in its own words. Each is written once where no template carries its key, so
 * the owner's edits survive a reseed; they are a starting point the owner edits.
 */
export const DISCOVERY_TEMPLATE_SEED: readonly DiscoveryTemplateSeed[] = [
  {
    key: 'panel-design', name: 'Panel Design', sortOrder: 10,
    description: 'Supply, load type, enclosure and the photos that set a new panel.',
    // 🔒 Decision 5550: the incoming supplies and the E-stops are Lists starting with one item each, and Panel Environment is a Group of the owner's four questions.
    questions: [
      q('supply-voltage-and-phase', 'Supply voltage and phase', 'Choice', ['120/240 V single phase', '208 V three phase', '480 V three phase', 'Other'], { groupKey: 'power-source' }),
      q('load-type', 'Load type', 'Choice', ['Heater or element', 'Motor', 'Other']),
      q('environment-at-panel', 'Environment at the panel location', 'Text'),
      q('communications', 'Communications protocol and network', 'Choice', ['Ethernet', 'Serial', 'Other']),
      q('e-stop-location', 'Location', 'Text', [], { groupKey: 'e-stop' }),
      q('outdoor', 'Is it outdoor?', 'YesNo', [], { groupKey: 'panel-environment' }),
      q('corrosive-gas', 'Is there corrosive gas present?', 'YesNo', [], { groupKey: 'panel-environment' }),
      q('explosive-gas', 'Are there any explosive gases present?', 'YesNo', [], { groupKey: 'panel-environment' }),
      q('dust-level', 'How dusty is the environment? 1 is a clean room, 5 is thick dust', 'Number', [], { groupKey: 'panel-environment', rangeFrom: 1, rangeTo: 5 }),
    ],
    uploadPoints: [
      // 🔒 T2771: the owner's own example of a tag's description.
      u('feeder-circuit', 'Feeder Circuit', 'Photo', { groupKey: 'power-source', description: 'The breaker or switchgear feeding the panel.' }), u('panel-overview', 'Panel Overview', 'Photo'), u('disconnect', 'Disconnect', 'Photo'), u('existing-drawings', 'Existing Drawings', 'File'), u('nameplate', 'Nameplate', 'Photo'),
      u('e-stop', 'E-stop', 'Photo', { groupKey: 'e-stop' }),
    ],
    groups: [g('power-source', 'List', 'Power source', 1), g('e-stop', 'List', 'E-stop', 1), g('panel-environment', 'Group', 'Panel Environment')],
  },
  {
    key: 'new-hmi', name: 'New HMI', sortOrder: 20,
    description: 'Screens wanted, with the current program or screen pictures to match.',
    questions: [q('screens-wanted', 'Screens wanted', 'Text'), q('keep-current-layout', 'Keep the current screen layout?', 'YesNo')],
    uploadPoints: [u('current-program', 'Current Program', 'File'), u('ui-pictures', 'UI Pictures', 'Photo')],
  },
  {
    key: 'feeder-circuit', name: 'Feeder Circuit', sortOrder: 30,
    description: 'Where the power comes from, its disconnect and its protection.',
    questions: [
      q('supply-voltage-and-phase', 'Supply voltage and phase', 'Choice', ['120/240 V single phase', '208 V three phase', '480 V three phase', 'Other']),
      q('supply-arrangement', 'Single incoming supply, or separate control feed?', 'Choice', ['Single supply', 'Separate control feed']),
      q('disconnect-reused', 'Is the existing disconnect reused or replaced?', 'Choice', ['Reused', 'Replaced']),
    ],
    uploadPoints: [u('feeder-circuit', 'Feeder Circuit', 'Photo', { description: 'The breaker or switchgear feeding the panel.' })],
  },
  {
    key: 'existing-drawings', name: 'Existing Drawings', sortOrder: 40,
    description: 'Schematics, I/O lists and panel drawings the customer holds.',
    questions: [],
    uploadPoints: [u('schematics', 'Schematics', 'File'), u('io-list', 'I/O List', 'File')],
  },
  {
    key: 'existing-panel-pictures', name: 'Existing Panel Pictures', sortOrder: 50,
    description: 'Tagged photos of the panel, nameplates and disconnect.',
    questions: [],
    uploadPoints: [u('panel-overview', 'Panel Overview', 'Photo'), u('nameplate', 'Nameplate', 'Photo'), u('disconnect', 'Disconnect', 'Photo'), u('machine', 'Machine', 'Photo')],
  },
  {
    key: 'current-program', name: 'Current Program', sortOrder: 60,
    description: 'The running program and what the new one must do as today.',
    questions: [q('program-available', 'Is the running program available?', 'YesNo'), q('same-as-today', 'What must the new system do exactly as today?', 'Text')],
    uploadPoints: [u('current-program', 'Current Program', 'File')],
  },
  {
    key: 'ui-pictures', name: 'UI Pictures', sortOrder: 70,
    description: 'Pictures of the screens operators use today.',
    questions: [],
    uploadPoints: [u('operator-screens', 'Operator Screens', 'Photo')],
  },
];
